/**
 * Phase 15.6J — Global Persistent Player.
 *
 * Single HTMLAudioElement owned by the provider. Tracks play across route
 * changes because the audio element lives in the React tree at the App
 * root, untouched by route renders. Surfaces call `usePlayer().play(track, opts)`.
 *
 * Counts a real play exactly once per (trackId × session) after 10s of
 * actual playback — same contract as the old per-page AudioPlayer.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";

const PlayerContext = createContext(null);

// Singles Store — Preview Lock (frontend enforcement).
// When the backend flags a track with `singles_locked=true`, the master
// `music_url` has already been redacted server-side. We MUST NOT play
// any cached full URL from a stale queue item, nor fall back to a
// legacy field. Instead, we route playback through the safe preview
// endpoint the backend advertises (`singles_preview_url` → a Cloudinary
// 30-45s excerpt). Owners / verified purchasers receive
// `singles_locked=false` and normal `music_url` — no code change needed.
const _absPreview = (path) => {
  if (!path || typeof path !== "string") return "";
  if (path.startsWith("http")) return path;
  return `${BACKEND_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

const normaliseTrack = (raw = {}) => {
  if (!raw) return null;
  const trackId = raw.trackId || raw.submission_id || raw.id || raw._id || null;
  const singlesLocked = raw.singles_locked === true;
  const singlesForSale = raw.singles_for_sale === true;
  const previewUrl = _absPreview(raw.singles_preview_url);
  // Hard-block full-master playback for locked tracks. We deliberately
  // ignore raw.music_url / raw.audio_url / raw.src / raw.url even if a
  // stale/cached card still carries it — the preview URL is the ONLY
  // legitimate source when the backend says the track is locked.
  let audioSrc = "";
  if (singlesLocked) {
    audioSrc = previewUrl;
  } else {
    const master = raw.music_url || raw.audio_url || raw.src || raw.url || "";
    audioSrc = typeof master === "string" && master.startsWith("http") ? master : "";
  }
  return {
    trackId,
    title: raw.track_title || raw.title || "Untitled track",
    artistName: raw.artist_name || raw.artistName || "Unknown artist",
    artistId: raw.user_id || raw.artist_id || null,
    coverUrl: raw.cover_url || raw.cover_image || raw.coverArt || raw.cover_art_url || raw.coverUrl || "",
    audioSrc,
    displayPlays: raw.display_plays ?? null,
    sourceLabel: raw.sourceLabel || null,
    // Singles metadata surfaced to consumers (mini-player, hero UI).
    singlesForSale,
    singlesLocked,
    singlesPriceUsd: raw.singles_price_usd ?? null,
    singlesListingId: raw.singles_listing_id || null,
    singlesBuyUrl: raw.singles_buy_url || (raw.singles_listing_id ? `/singles/${raw.singles_listing_id}` : null),
    singlesPreviewUrl: previewUrl,
    raw,
  };
};

export const PlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  if (audioRef.current === null && typeof window !== "undefined") {
    const el = new window.Audio();
    el.preload = "metadata";
    audioRef.current = el;
  }

  const [track, setTrack] = useState(null);
  const [queue, setQueue] = useState([]); // normalised tracks
  const [queueIndex, setQueueIndex] = useState(-1);
  const [sourceLabel, setSourceLabel] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [mediaError, setMediaError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  // Track 10s play-count rule.
  const countedRef = useRef(new Set());

  // Wire audio events once.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setCurrentTime(el.currentTime || 0);
    const onDur = () => {
      if (!isNaN(el.duration) && el.duration > 0) setDuration(el.duration);
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onError = () => { setIsPlaying(false); setMediaError("playback_failed"); };
    const onEnded = () => {
      setIsPlaying(false);
      // auto-advance
      setQueueIndex((idx) => {
        const next = idx + 1;
        const list = queueRef.current;
        if (list && next < list.length) {
          _playInternal(list[next], list, next, sourceLabelRef.current);
          return next;
        }
        return idx;
      });
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onDur);
    el.addEventListener("durationchange", onDur);
    el.addEventListener("canplay", onDur);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("error", onError);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onDur);
      el.removeEventListener("durationchange", onDur);
      el.removeEventListener("canplay", onDur);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("error", onError);
      el.removeEventListener("ended", onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refs for queue/source that the audio event handlers can read fresh.
  const queueRef = useRef([]);
  const sourceLabelRef = useRef(null);
  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { sourceLabelRef.current = sourceLabel; }, [sourceLabel]);

  // Count play once per trackId per session after 10s of playback.
  useEffect(() => {
    if (!track?.trackId) return;
    if (currentTime < 10) return;
    if (countedRef.current.has(track.trackId)) return;
    // Singles Store — locked previews must NOT increment play counters.
    // They are 30-45s Cloudinary excerpts, not real listens.
    if (track.singlesLocked) return;
    countedRef.current.add(track.trackId);
    // Phase 19.8 — Beats use /api/beats/{beat_id}/play; tracks use the
    // existing track play-count endpoint. Both are fire-and-forget.
    //
    // iter_225 · Phase 22.0 — Attribute plays that originated from a
    // Featured placement so /me/boost-pulse can report qualifying
    // "Plays from Feature". The tag flows from the queue-item's
    // `feed_source` ("featured"), the `sourceLabel` we set for the UI
    // ribbon, or the raw payload's `feed_source`. Never duplicates a
    // play — the backend ONLY $inc's a separate `feature_plays`
    // counter alongside the single `play_count` increment.
    const rawSrc = track.raw?.feed_source
      || track.sourceLabel
      || sourceLabelRef.current
      || "";
    const isFromFeature = /featur|spotlight/i.test(String(rawSrc));
    const featureQS = isFromFeature ? "?source=feature" : "";
    if (track.raw?.kind === "beat" && track.raw?.beat_id) {
      api.post(`/beats/${track.raw.beat_id}/play`).catch(() => {});
    } else {
      api.post(`/track/${track.trackId}/play-count${featureQS}`).catch(() => {});
    }
  }, [currentTime, track]);

  const _playInternal = async (normTrack, list, idx, label) => {
    const el = audioRef.current;
    if (!el || !normTrack) return;
    setTrack(normTrack);
    setQueue(list);
    setQueueIndex(idx);
    setSourceLabel(label || normTrack.sourceLabel || null);
    setMediaError(null);
    setCurrentTime(0);
    setDuration(0);
    setIsVisible(true);
    let src = normTrack.audioSrc;
    // Lazy-fetch music_url when card data lacked it. Beats and tracks live
    // on different endpoints — branch by the marker we stash in raw.kind.
    if (!src && normTrack.trackId) {
      try {
        const isBeat = normTrack.raw?.kind === "beat" && normTrack.raw?.beat_id;
        const r = isBeat
          ? await api.get(`/beats/${normTrack.raw.beat_id}`)
          : await api.get(`/track/${normTrack.trackId}`);
        const sub = r.data?.submission || r.data?.beat || r.data || {};
        // Singles Store — if the freshly-fetched submission is locked,
        // the backend has redacted music_url. Route through the safe
        // preview URL instead. NEVER fall back to a cached master URL.
        if (sub.singles_locked === true) {
          const preview = _absPreview(sub.singles_preview_url);
          if (preview) {
            src = preview;
            setTrack((t) => (t && t.trackId === normTrack.trackId
              ? { ...t, audioSrc: preview, singlesLocked: true, singlesForSale: true,
                  singlesPreviewUrl: preview,
                  singlesPriceUsd: sub.singles_price_usd ?? t.singlesPriceUsd,
                  singlesListingId: sub.singles_listing_id || t.singlesListingId,
                  singlesBuyUrl: sub.singles_buy_url || t.singlesBuyUrl,
                  coverUrl: t.coverUrl || sub.cover_url || sub.cover_art || "" }
              : t));
          }
        } else {
          const remote = sub.music_url || sub.audio_url || "";
          if (typeof remote === "string" && remote.startsWith("http")) {
            src = remote;
            // Patch state so future controls reflect the resolved src.
            setTrack((t) => (t && t.trackId === normTrack.trackId ? { ...t, audioSrc: remote, coverUrl: t.coverUrl || sub.cover_url || sub.cover_art || "" } : t));
          }
        }
      } catch (_e) { /* fall through to error UI */ }
    }
    if (!src) {
      setMediaError("audio_missing");
      setIsPlaying(false);
      return;
    }
    if (el.src !== src) el.src = src;
    el.play().catch(() => { /* browser blocked; user can retry */ });
  };

  const play = useCallback((rawTrack, opts = {}) => {
    const norm = normaliseTrack(rawTrack);
    if (!norm) return;
    // Build queue from opts.queue if provided, otherwise single-track queue.
    let list = [norm];
    let idx = 0;
    if (Array.isArray(opts.queue) && opts.queue.length > 0) {
      list = opts.queue.map(normaliseTrack).filter(Boolean);
      idx = list.findIndex((t) => t.trackId === norm.trackId);
      if (idx < 0) { list = [norm, ...list]; idx = 0; }
    }
    _playInternal(norm, list, idx, opts.sourceLabel || null);
  }, []);

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el || !track) return;
    if (el.paused) {
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  }, [track]);

  const seekTo = useCallback((seconds) => {
    const el = audioRef.current;
    if (!el || !isFinite(seconds)) return;
    el.currentTime = Math.max(0, Math.min(seconds, el.duration || seconds));
  }, []);

  const seekToPercent = useCallback((pct) => {
    const el = audioRef.current;
    if (!el || !isFinite(pct)) return;
    if (el.duration > 0) el.currentTime = (pct / 100) * el.duration;
  }, []);

  const next = useCallback(() => {
    const list = queueRef.current;
    setQueueIndex((idx) => {
      const n = idx + 1;
      if (list && n < list.length) {
        _playInternal(list[n], list, n, sourceLabelRef.current);
        return n;
      }
      return idx;
    });
  }, []);

  const prev = useCallback(() => {
    const el = audioRef.current;
    if (el && el.currentTime > 3) { el.currentTime = 0; return; }
    const list = queueRef.current;
    setQueueIndex((idx) => {
      const p = idx - 1;
      if (list && p >= 0) {
        _playInternal(list[p], list, p, sourceLabelRef.current);
        return p;
      }
      return idx;
    });
  }, []);

  const close = useCallback(() => {
    const el = audioRef.current;
    if (el) { el.pause(); el.removeAttribute("src"); el.load(); }
    setTrack(null);
    setQueue([]);
    setQueueIndex(-1);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setMediaError(null);
    setSourceLabel(null);
    setIsVisible(false);
  }, []);

  const retry = useCallback(() => {
    const el = audioRef.current;
    if (!el || !track?.audioSrc) return;
    setMediaError(null);
    el.load();
    el.play().catch(() => {});
  }, [track]);

  const hasNext = queueIndex >= 0 && queueIndex < queue.length - 1;
  const hasPrev = queueIndex > 0;

  const value = {
    track,
    queue,
    queueIndex,
    sourceLabel,
    isPlaying,
    currentTime,
    duration,
    mediaError,
    isVisible,
    hasNext,
    hasPrev,
    play,
    toggle,
    seekTo,
    seekToPercent,
    next,
    prev,
    close,
    retry,
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => {
  const ctx = useContext(PlayerContext);
  if (!ctx) {
    // Safe no-op when provider missing (e.g. unit tests).
    return {
      track: null, queue: [], queueIndex: -1, sourceLabel: null,
      isPlaying: false, currentTime: 0, duration: 0, mediaError: null,
      isVisible: false, hasNext: false, hasPrev: false,
      play: () => {}, toggle: () => {}, seekTo: () => {}, seekToPercent: () => {},
      next: () => {}, prev: () => {}, close: () => {}, retry: () => {},
    };
  }
  return ctx;
};

export default PlayerProvider;
